import type { FC } from 'react'
import { useEffect, useMemo } from 'react'

import { Select, Input } from '@skytech/ui'
import { useDispatch, useSelector } from 'react-redux'

import Button from '@/components/form/Button'
import { Translation } from '@/components/i18n/Translation'
import type { SmsType } from '@/components/util/SmsLogItem'
import { SmsLogItem } from '@/components/util/SmsLogItem'
import {
  getEcomOrderPhoneNumber,
  getEcomOrderSmsLog,
  getEcomOrderUser,
  getIsLoading,
  getReceiverOptions,
  getSelectedAlias,
  getSelectedReceiver,
  getSmsInputText,
  sendEcomOrderSms,
  setAlias,
  setSelectedReceiver,
  setSmsInputText,
} from '@/ducks/ecomOrderReducer'
import { useProfile } from '@/hooks/user'

export const EcomOrderCommunications: FC = () => {
  const dispatch = useDispatch()
  const aliases = useProfile()?.aliases

  const endUserPhoneNumber = useSelector(getEcomOrderPhoneNumber)
  const createdByUserPhoneNumber = useSelector(getEcomOrderUser)?.phone_number
  const orderSmsLog = useSelector(getEcomOrderSmsLog)

  const smsInputText = useSelector(getSmsInputText)
  const selectedAlias = useSelector(getSelectedAlias)
  const selectedReceiver = useSelector(getSelectedReceiver)
  const receiverOptions = useSelector(getReceiverOptions)

  const isLoading = useSelector(getIsLoading)
  const isDisabled = !receiverOptions || receiverOptions.length < 1 || isLoading

  useEffect(() => {
    !selectedAlias && aliases && aliases.length > 0 && dispatch(setAlias(aliases[0].id))
    // Set default receiver as End user
    !selectedReceiver &&
      receiverOptions.length > 0 &&
      dispatch(setSelectedReceiver(receiverOptions[0].value))
  }, [selectedAlias, aliases, receiverOptions, dispatch, selectedReceiver])

  const smsLog = useMemo(
    () =>
      orderSmsLog.filter((sms: SmsType) => {
        if (selectedReceiver === 'order_user') {
          return sms.receiver === String(createdByUserPhoneNumber)
        }
        return sms.receiver === String(endUserPhoneNumber)
      }),
    [selectedReceiver, orderSmsLog, createdByUserPhoneNumber, endUserPhoneNumber]
  )

  if (!aliases || aliases?.length < 1) return null

  return (
    <div className="mt-8">
      <h3>
        <Translation id="Communications" />
      </h3>

      <form>
        {receiverOptions.length > 0 && (
          <Select
            id="sms-receiver"
            options={receiverOptions}
            value={selectedReceiver}
            label={<Translation id="To:" />}
            marginBottom={16}
            onChange={(value: any) => {
              dispatch(setSelectedReceiver(value.value))
            }}
          />
        )}

        {aliases && aliases.length > 1 && (
          <Select
            id="sms-sender"
            options={
              aliases?.map(alias => ({
                value: alias.id,
                label: alias.alias,
              })) || []
            }
            value={selectedAlias}
            label={<Translation id="Sender:" />}
            marginBottom={16}
            onChange={(value: any) => {
              dispatch(setAlias(value.value))
            }}
          />
        )}

        <Input
          id="sms-message"
          label={<Translation id="New message" />}
          value={smsInputText}
          isDisabled={isDisabled}
          marginBottom={16}
          onChange={e => dispatch(setSmsInputText(e.target.value))}
        />

        <Button
          type="button"
          // @ts-ignore
          onClick={() => dispatch(sendEcomOrderSms())}
          disabled={isDisabled || !smsInputText}
        >
          <Translation id="Send sms" />
        </Button>
      </form>

      {smsLog.length > 0 && (
        <div>
          {smsLog.map((sms: SmsType) => (
            <SmsLogItem key={sms.sent} {...sms} />
          ))}
        </div>
      )}
    </div>
  )
}
